import { useContext, FC, PropsWithChildren, useState, useCallback, createContext } from 'react';
import noop from 'utils/noop';
import { useGetIntegrations } from '../hooks/useGetIntegrations';
import { MovableInkChildIntegration, MovableInkIntegration } from '../types';
import { useGetChildIntegrations } from '../hooks/useGetChildIntegrations';

export type MovableInkIntegrationContextState = {
  integrations: MovableInkIntegration[];
  isFetchingIntegrations: boolean;
  selectedIntegration?: MovableInkIntegration;
  setSelectedIntegration: (integration: MovableInkIntegration, cb?: () => void) => void;
  childIntegrations: MovableInkChildIntegration[];
  currentCompanyId?: MovableInkChildIntegration['companyId'];
  isFetchingChildIntegrations: boolean;
  setCurrentCompanyId: (companyId?: MovableInkChildIntegration['companyId']) => void;
  hasIntegrations: boolean;
};

export const defaultState = {
  integrations: [],
  isFetchingIntegrations: false,
  setSelectedIntegration: noop,
  childIntegrations: [],
  isFetchingChildIntegrations: false,
  setCurrentCompanyId: noop,
  hasIntegrations: false,
};

export const MovableInkIntegrationContext =
  createContext<MovableInkIntegrationContextState>(defaultState);

export const useMovableInkIntegrationContext = () => useContext(MovableInkIntegrationContext);

export const MovableInkIntegrationContextProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [selectedIntegration, updateSelectedIntegration] = useState<MovableInkIntegration>();
  const [currentCompanyId, setCurrentCompanyId] =
    useState<MovableInkChildIntegration['companyId']>();

  const { data: integrations = [], isLoading: isFetchingIntegrations } = useGetIntegrations();
  const { data: childIntegrations = [], isLoading: isFetchingChildIntegrations } =
    useGetChildIntegrations(selectedIntegration?.tokenId);

  const setSelectedIntegration = useCallback(
    (integration: MovableInkIntegration, cb?: () => void) => {
      updateSelectedIntegration(integration);
      if (cb) cb();
    },
    [updateSelectedIntegration],
  );

  const hasIntegrations = !isFetchingIntegrations && integrations.length > 0;

  const value = {
    integrations,
    childIntegrations,
    isFetchingIntegrations,
    isFetchingChildIntegrations,
    selectedIntegration,
    currentCompanyId,
    hasIntegrations,
    setSelectedIntegration,
    setCurrentCompanyId,
  };

  return (
    <MovableInkIntegrationContext.Provider value={value}>
      {children}
    </MovableInkIntegrationContext.Provider>
  );
};
